import { action } from "typesafe-actions";
import { ICSIOEDocs } from "./csioEdocs.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import { makeApiCall } from "../../helpers/postRequest";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { ERROR_MESSAGES } from "../../constants/enums";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { api } from "../../api/api";
import { uploadFile } from "../../features/FileUpload/utils";

export const FETCH_CSIO_EDOCS_LIST_PROGRESS = "FETCH_CSIO_EDOCS_LIST_PROGRESS";
export const FETCH_CSIO_EDOCS_LIST_SUCCESS = "FETCH_CSIO_EDOCS_LIST_SUCCESS";
export const FETCH_CSIO_EDOCS_LIST_FAILED = "FETCH_CSIO_EDOCS_LIST_FAILED";

export const fetchCsioEdocsListProgress = () =>
  action(FETCH_CSIO_EDOCS_LIST_PROGRESS);
export const fetchCsioEdocsListSuccess = (
  data: ICSIOEDocs[],
  totalRecords: number
) =>
  action(FETCH_CSIO_EDOCS_LIST_SUCCESS, {
    data,
    totalRecords,
  });
export const fetchCsioEdocsListFailed = () =>
  action(FETCH_CSIO_EDOCS_LIST_FAILED);

export const fetchCsioEdocsListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    searchValue: IDatatableV2AdvancedSearchFilter,
    customerPolicyId?: number,
    type?: string,
    edocType?: string,
    actionState?: { action_needed: string; action_status: string }
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      dispatch(fetchCsioEdocsListProgress());

      let finalUrl = `/general/get-notice-and-edocs?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}&columnList=notice_and_edoc_code&columnList=record_type&columnList=status&columnList=message_guid&columnList=customer_name&columnList=policy_number&columnList=type&columnList=email&columnList=business_purpose_type&columnList=insurance_company&columnList=line_of_business&columnList=producer_code&columnList=branch_code&columnList=create_ts&columnList=insert_ts&columnList=due_date&columnList=notice_type&columnList=message_type&columnList=attachment&columnList=customer_id&columnList=customer_policy_id&columnList=due_time&columnList=assigned_to_id&columnList=action_taken&columnList=mobile_number&columnList=description&columnList=financed&columnList=financed_by&columnList=contract_term_effective_date&columnList=contract_term_expiry_date&columnList=action_needed&columnList=action_status&columnList=remarks&columnList=created_by_name&columnList=modified_by_name`;
      if (status !== "-1") {
        finalUrl += "&status=" + status;
      }
      if (type !== "-1") {
        finalUrl += "&column=record_type&value=" + type;
      }

      if (customerPolicyId) {
        finalUrl += "&customer_policy_id=" + customerPolicyId;
      }
      const advanceFilters: any[] = [];
       // 1️⃣ From searchValue
       if (searchValue.length > 0) {
        // assuming searchValue is already an array of filters
        advanceFilters.push(...searchValue);
      }

      // 2️⃣ From edocType
      if (edocType) {
        advanceFilters.push({
          column: ["type"],
          operator: "EQUAL",
          value: edocType,
          logicalOperator: "AND",
        });
      }

      // 3️⃣ From action_needed
      if (actionState?.action_needed && actionState.action_needed !== "-1") {
        advanceFilters.push({
          column: ["action_needed"],
          operator: "EQUAL",
          value: actionState.action_needed,
          logicalOperator: "AND",
        });
      }

      // 4️⃣ From action_status
      if (actionState?.action_status && actionState.action_status !== "-1") {
        advanceFilters.push({
          column: ["action_status"],
          operator: "EQUAL",
          value: actionState.action_status,
          logicalOperator: "AND",
        });
      }

      // 5️⃣ Finally append ONCE
      if (advanceFilters.length > 0) {
        finalUrl += `&advanceFilter=${encodeURIComponent(
          JSON.stringify(advanceFilters)
        )}`;
      }
      const res = await makeApiCall({
        method: "GET",
        url: finalUrl,
      });
      const data: ICSIOEDocs[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchCsioEdocsListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchCsioEdocsListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchCsioEdocsListFromArchiveAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    searchValue: IDatatableV2AdvancedSearchFilter,
    customerPolicyId?: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      dispatch(fetchCsioEdocsListProgress());

      let finalUrl = `/general/get-notice-and-edocs-archive?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}&columnList=notice_and_edoc_code&columnList=record_type&columnList=status&columnList=message_guid&columnList=customer_name&columnList=policy_number&columnList=type&columnList=email&columnList=business_purpose_type&columnList=insurance_company&columnList=line_of_business&columnList=producer_code&columnList=branch_code&columnList=create_ts&columnList=insert_ts&columnList=due_date&columnList=notice_type&columnList=message_type&columnList=attachment&columnList=customer_id&columnList=customer_policy_id&columnList=due_time&columnList=assigned_to_id&columnList=action_taken&columnList=mobile_number&columnList=description&columnList=financed&columnList=financed_by&columnList=contract_term_effective_date&columnList=contract_term_expiry_date&columnList=action_needed&columnList=action_status&columnList=remarks`;
      if (searchValue.length > 0) {
        finalUrl = `/general/get-notice-and-edocs-archive?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${
          date.fromDate
        }&to_date=${date.toDate}&advanceFilter=${JSON.stringify(searchValue)}`;
      }
      if (status !== "-1") {
        finalUrl += "&status=" + status;
      }

      if (customerPolicyId) {
        finalUrl += "&customer_policy_id=" + customerPolicyId;
      }
      const res = await makeApiCall({
        method: "GET",
        url: finalUrl,
      });
      const data: ICSIOEDocs[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchCsioEdocsListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchCsioEdocsListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const updateCSIOEdocsStatus =
  (
    keys: number[],
    status: string,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());

      await api.put(`/csio/update-csio-edocs-status`, {
        primary_key: keys,
        status: status,
      });
      dispatch(
        showMessage({
          displayAs: "snackbar",
          message: "eDocs reviewed successfully.",
          type: "success",
        })
      );

      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_CSIO_EDOCS_PROGRESS = "FETCH_CSIO_EDOCS_PROGRESS";
export const FETCH_CSIO_EDOCS_SUCCESS = "FETCH_CSIO_EDOCS_SUCCESS";
export const FETCH_CSIO_EDOCS_FAILED = "FETCH_CSIO_EDOCS_FAILED";

export const fetchCsioEdocsProgress = () => action(FETCH_CSIO_EDOCS_PROGRESS);
export const fetchCsioEdocsSuccess = (data: ICSIOEDocs) =>
  action(FETCH_CSIO_EDOCS_SUCCESS, {
    data,
  });
export const fetchCsioEdocsFailed = (errorMessage: string) =>
  action(FETCH_CSIO_EDOCS_FAILED, { errorMessage });

export const fetchCsioEdocsAsync =
  (
    identifier: string,
    type: "guid" | "code"
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchCsioEdocsProgress());

      let finalUrl = `/general/get-notice-and-edocs`;

      if (type === "guid") {
        finalUrl = finalUrl + `?column=message_guid&value=${identifier}`;
      } else {
        finalUrl =
          finalUrl + `?column=notice_and_edoc_code&value=${identifier}`;
      }

      const res = await makeApiCall({
        method: "GET",
        url: finalUrl,
      });
      const data = res.data.data;

      if (data.length > 0) {
        dispatch(fetchCsioEdocsSuccess(data[0]));
      } else {
        dispatch(
          fetchCsioEdocsFailed(
            "Unfortunately, there are no records available at the moment."
          )
        );
      }
    } catch (err: any) {
      dispatch(fetchCsioEdocsFailed("Something went to be wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const FETCH_MANAGE_CSIO_EDOCS_PROGRESS =
  "FETCH_MANAGE_CSIO_EDOCS_PROGRESS";
export const FETCH_MANAGE_CSIO_EDOCS_SUCCESSS =
  "FETCH_MANAGE_CSIO_EDOCS_SUCCESSS";
export const FETCH_MANAGE_CSIO_EDOCS_FAILED = "FETCH_MANAGE_CSIO_EDOCS_FAILED";

export const fetchManageCsioEdocsProgress = () =>
  action(FETCH_MANAGE_CSIO_EDOCS_PROGRESS);
export const fetchManageCsioEdocsSuccess = (data: ICSIOEDocs) =>
  action(FETCH_MANAGE_CSIO_EDOCS_SUCCESSS, { data });
export const fetchManageCsioEdocsFailed = (error?: string) =>
  action(FETCH_MANAGE_CSIO_EDOCS_FAILED, { error });

export const fetchManageCsioEdocsAsync =
  (messageGUid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchManageCsioEdocsProgress());

      const res = await makeApiCall({
        method: "GET",
        url: `/csio/get-csio-retrieve-messages?message_guid=${messageGUid}`,
      });

      if (res && res.data && res.data.data) {
        const data = res.data.data;

        if (data.length > 0) {
          const { xml_json_message, attachment } = data[0];
          try {
            const parsedData = JSON.parse(xml_json_message);
            dispatch(fetchManageCsioEdocsSuccess(parsedData));
          } catch (parseError) {
            dispatch(
              fetchManageCsioEdocsFailed("Failed to parse JSON message")
            );
          }
        } else {
          console.log("No record found.");
          dispatch(fetchManageCsioEdocsFailed(ERROR_MESSAGES.NO_RECORD_FOUND));
        }
      } else {
        console.log("No record found in response data.");
        dispatch(fetchManageCsioEdocsFailed(ERROR_MESSAGES.NO_RECORD_FOUND));
      }
    } catch (err: any) {
      console.error("Fetch error:", err);
      dispatch(fetchManageCsioEdocsFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const upsertCsioEdocsAsync =
  (
    data: ICSIOEDocs,
    file: File | null,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const asPayload = {
        policy_number: data.policy_number,
      };
      const path = await uploadFile(
        file,
        "CSIO",
        data.attachment || "",
        asPayload
      );
      await makeApiCall(
        {
          url: "/csio/create-csio-edocs",
          method: "POST",
          // automation: {
          //   isUpdate: isUpdate,
          //   primaryFieldName: "brokerage_equipment_code",
          // },
        },
        {},
        {
          ...data,
          csio_edoc_code: data.notice_and_edoc_code,
          attachment: path,
        }
      );

      dispatch(
        showMessage({
          type: "success",
          message: "CSIO EDOCS saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const CLEAR_CSIO_EDOCS_STATE = "CLEAR_CSIO_EDOCS_STATE";
export const clearCsioEdocsState = () => action(CLEAR_CSIO_EDOCS_STATE);
